// V4.12 Phase F — persistent Market Canvas board (desktop right column).
// V4.23 — premium workspace frames: pin, remove, minimize/expand,
// result-set label with hit count + provenance ("Aus: <AI-Frage>").
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  BarChart3, ChevronDown, ChevronUp, GitCompareArrows, Layers,
  LayoutGrid, List, Map as MapIcon, Pin, TrendingUp, X,
} from "lucide-react";
import {
  clearCanvas, deleteCanvasWidget, fetchCanvas, setCanvasWidgetCollapsed,
  setCanvasWidgetPinned,
} from "../../api/aiApi";
import { WIDGET_RENDERERS } from "./ChatWidgets";

const TYPE_ICONS = {
  listings: List,
  listing_compare: GitCompareArrows,
  mini_map: MapIcon,
  price_distribution: BarChart3,
  scatter: TrendingUp,
  region_compare: GitCompareArrows,
  market_overview: BarChart3,
  listing_groups: Layers,
};

function frameTitle(t, item) {
  const w = item.payload || {};
  let key = item.type;
  if (item.type === "scatter") {
    key = w.x_key === "mileage_km" ? "scatter_auto" : "scatter_immo";
  }
  const base = t(`canvas.widgetTitles.${key}`, {
    defaultValue: t("canvas.widgetTitles.generic"),
  });
  const label = w.label || w.region
    || (w.title_key ? t(`widgets.titles.${w.title_key}`, { defaultValue: w.title }) : w.title);
  return label ? `${base} · ${label}` : base;
}

function frameCount(item) {
  const w = item.payload || {};
  if (item.type === "listings" || item.type === "listing_groups") {
    return w.total || null;
  }
  return null;
}

export const CanvasBoard = () => {
  const { t } = useTranslation("ai");
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["ai-canvas"],
    queryFn: fetchCanvas,
    staleTime: 30 * 1000,
    retry: false,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["ai-canvas"] });
  const pinMut = useMutation({
    mutationFn: ({ ulid, pinned }) => setCanvasWidgetPinned(ulid, pinned),
    onSuccess: invalidate,
  });
  const collapseMut = useMutation({
    mutationFn: ({ ulid, collapsed }) => setCanvasWidgetCollapsed(ulid, collapsed),
    // optimistic — a minimize must feel instant
    onMutate: async ({ ulid, collapsed }) => {
      await qc.cancelQueries({ queryKey: ["ai-canvas"] });
      qc.setQueryData(["ai-canvas"], (old) => ({
        ...(old || {}),
        items: ((old && old.items) || []).map((it) =>
          it.ulid === ulid ? { ...it, collapsed } : it),
      }));
    },
    onSettled: invalidate,
  });
  const rmMut = useMutation({ mutationFn: deleteCanvasWidget, onSuccess: invalidate });
  const clearMut = useMutation({ mutationFn: clearCanvas, onSuccess: invalidate });
  const items = q.data?.items || [];

  if (q.isLoading) {
    return (
      <div className="space-y-4" data-testid="ai-canvas-skeleton">
        {[0, 1].map((i) => (
          <div key={i} className="animate-pulse space-y-2">
            <div className="h-3 w-32 rounded bg-zinc-800/80" />
            <div className="h-36 rounded-2xl border border-zinc-800/60 bg-zinc-900/50" />
          </div>
        ))}
      </div>
    );
  }

  if (!q.isLoading && items.length === 0) {
    return (
      <div
        data-testid="ai-canvas-empty"
        className="rounded-2xl border border-dashed border-zinc-800 p-6 text-center text-xs leading-relaxed text-zinc-600"
      >
        {t("canvas.empty")}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ai-canvas-board">
      {items.length > 0 && (
        <div className="flex items-center justify-between text-[11px] text-zinc-600">
          <span data-testid="ai-canvas-count">
            {t("canvas.itemCount", { count: items.length })}
          </span>
          <button
            type="button"
            data-testid="ai-canvas-clear-btn"
            onClick={() => clearMut.mutate()}
            className="transition hover:text-zinc-300"
          >
            {t("canvas.clearAll")}
          </button>
        </div>
      )}
      {items.map((item) => {
        const R = WIDGET_RENDERERS[item.type];
        if (!R) return null;
        const Icon = TYPE_ICONS[item.type] || LayoutGrid;
        const count = frameCount(item);
        const collapsed = !!item.collapsed;
        return (
          <div
            key={item.ulid}
            className="group"
            data-testid={`ai-canvas-frame-${item.ulid}`}
            data-widget-type={item.type}
          >
            <div className="mb-1 flex items-center gap-1.5 px-0.5">
              <Icon className="h-3 w-3 shrink-0 text-amber-400/70" />
              <span className="flex-1 truncate text-[10px] uppercase tracking-wide text-zinc-600">
                {frameTitle(t, item)}
              </span>
              {count != null && (
                <span
                  data-testid={`ai-canvas-total-${item.ulid}`}
                  className="rounded bg-zinc-800/80 px-1.5 text-[9px] font-semibold text-zinc-400"
                >
                  {t("canvas.hitCount", { count })}
                </span>
              )}
              {item.payload?.country && (
                <span
                  data-testid={`ai-canvas-country-${item.ulid}`}
                  className="rounded border border-zinc-700/70 px-1 text-[9px] font-semibold uppercase text-zinc-500"
                >
                  {item.payload.country}
                </span>
              )}
              <button
                type="button"
                data-testid={`ai-canvas-collapse-${item.ulid}`}
                aria-pressed={collapsed}
                title={collapsed ? t("canvas.expand") : t("canvas.collapse")}
                onClick={() =>
                  collapseMut.mutate({ ulid: item.ulid, collapsed: !collapsed })}
                className="text-zinc-700 opacity-60 transition hover:text-zinc-300 group-hover:opacity-100"
              >
                {collapsed
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronUp className="h-3.5 w-3.5" />}
              </button>
              <button
                type="button"
                data-testid={`ai-canvas-pin-${item.ulid}`}
                aria-pressed={item.pinned}
                title={item.pinned ? t("canvas.unpin") : t("canvas.pin")}
                onClick={() => pinMut.mutate({ ulid: item.ulid, pinned: !item.pinned })}
                className={`transition ${
                  item.pinned
                    ? "text-amber-400"
                    : "text-zinc-700 opacity-60 hover:text-zinc-300 group-hover:opacity-100"
                }`}
              >
                <Pin className={`h-3.5 w-3.5 ${item.pinned ? "fill-current" : ""}`} />
              </button>
              <button
                type="button"
                data-testid={`ai-canvas-remove-${item.ulid}`}
                title={t("canvas.remove")}
                onClick={() => rmMut.mutate(item.ulid)}
                className="text-zinc-700 opacity-60 transition hover:text-rose-300 group-hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {item.source_query && (
              <div
                data-testid={`ai-canvas-source-${item.ulid}`}
                className="mb-1 truncate px-0.5 text-[10px] italic text-zinc-700"
              >
                {t("canvas.from")}: „{item.source_query}“
              </div>
            )}
            {!collapsed && <R widget={item.payload} variant="canvas" />}
          </div>
        );
      })}
    </div>
  );
};
